/**
 * QuestionMatrix
 *
 * P0 — A sortable / filterable matrix that surfaces ALL extracted tender
 * questions with their state (refined, drafted, needs draft, excluded) and
 * the per-question grounded improvement_score from the backend rubric.
 * Replaces the previous "14 of 14" linear list which silently dropped the
 * questions without inline bidder responses.
 *
 * Reads:
 *   • analysisData.questions
 *   • analysisData.refined_questions  (state.refined_questions, scored)
 *   • analysisData.drafted_questions  (state.drafted_questions, scored)
 *   • analysisData.evaluation_criteria
 *   • analysisData.question_selection_metadata
 *
 * Renders:
 *   • Stats overview (total / refined / drafted / needs_draft, average score)
 *   • Search + filter (status, type, criterion) + sort
 *   • Compliance Checklist (P6) as an integrated section
 *   • CSV export (P8 lite — Excel format mirrors what evaluators expect)
 *   • Click any row → expands inline below the row with full explainability
 */

import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Search,
  Download,
  FileText,
  Sparkles,
  Edit3,
  AlertCircle,
  Eye,
  Filter,
  ArrowUpDown,
  ChevronRight,
  ChevronDown,
  X,
} from 'lucide-react';
import { theme } from '../styles/theme';
import {
  ComplianceItem,
  DraftedQuestion,
  EvaluationCriteria,
  PastTenderMatch,
  Question,
  QuestionMatrixRow,
  QuestionSelectionMetadata,
  RefinedResponse,
  WorkflowWarning,
} from '../types';
import {
  buildQuestionMatrix,
  STATUS_SORT_ORDER,
  questionMatrixToCsv,
} from '../utils/dataTransformers';
import {
  countWords,
  estimateWordBudgetFromPages,
  extractPageLimitFromText,
} from '../utils/refinementUtils';
import QuestionDetailDrawer from './QuestionDetailDrawer';
import ComplianceChecklist from './ComplianceChecklist';
import { aiApiService } from '../services/aiApi';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[4]};
`;

const OverviewSection = styled.section`
  background: linear-gradient(135deg, ${theme.colors.success[100]}20, ${theme.colors.primary[100]}20);
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
`;

const OverviewHeader = styled.header`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const OverviewIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.success[500]});
  border-radius: ${theme.borderRadius.md};
  color: white;
`;

const OverviewContent = styled.div`
  flex: 1;
  h2 {
    margin: 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.xl};
  }
  p {
    margin: ${theme.spacing[1]} 0 0 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${theme.spacing[3]};
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: ${theme.spacing[1]};
`;

const ControlsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 240px;
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: 0 ${theme.spacing[3]};
  &:focus-within { border-color: ${theme.colors.primary[400]}; }

  svg { color: ${theme.colors.textMuted}; flex-shrink: 0; }
  input {
    flex: 1;
    border: none;
    outline: none;
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textPrimary};
    background: transparent;
  }
`;

const Select = styled.select`
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
  cursor: pointer;
  &:focus { outline: none; border-color: ${theme.colors.primary[400]}; }
`;

const ExportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background ${theme.transitions.base};
  &:hover { background: ${theme.colors.primary[600]}; }
`;

const TableWrapper = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.fontSize.sm};
`;

const Th = styled.th<{ $sortable?: boolean }>`
  text-align: left;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${theme.colors.border};
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.xs};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  &:hover {
    background: ${({ $sortable }) =>
      $sortable ? theme.colors.surfaceHover : theme.colors.backgroundSecondary};
  }
`;

const Td = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  vertical-align: top;
  color: ${theme.colors.textPrimary};
`;

const Tr = styled.tr<{ $expanded?: boolean }>`
  cursor: pointer;
  transition: background ${theme.transitions.base};
  background: ${({ $expanded }) =>
    $expanded ? theme.colors.primary[50] : 'transparent'};
  &:hover { background: ${({ $expanded }) =>
    $expanded ? theme.colors.primary[50] : theme.colors.surfaceHover}; }
  &:last-child td { border-bottom: none; }
`;

/* Inline expansion row — appears directly under the clicked row, sharing the
 * full table width via colSpan. The accent border-left keeps the spatial tie
 * to its parent row (Linear/Stripe pattern). */
const ExpandedRow = styled.tr`
  background: ${theme.colors.backgroundSecondary};
  & > td {
    padding: 0;
    border-bottom: 1px solid ${theme.colors.border};
  }
`;

const ExpandedCell = styled.div`
  padding: ${theme.spacing[5]} ${theme.spacing[6]};
  border-left: 3px solid ${theme.colors.primary[400]};
  background: linear-gradient(
    180deg,
    ${theme.colors.primary[50]} 0%,
    ${theme.colors.surface} 80px
  );
  animation: expand-fade 0.18s ease-out;

  @keyframes expand-fade {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const ChevronIcon = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  transition: transform 0.18s ease;
  transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});
`;

const QId = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${theme.colors.backgroundSecondary};
  color: ${theme.colors.textMuted};
  border-radius: ${theme.borderRadius.sm};
  font-family: ${theme.typography.fontFamily.mono};
  font-size: ${theme.typography.fontSize.xs};
`;

const TypeBadge = styled.span<{ $tone: 'success' | 'warning' | 'info' | 'neutral' }>`
  display: inline-block;
  padding: 1px 8px;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  ${({ $tone }) => {
    switch ($tone) {
      case 'success':
        return `background:${theme.colors.success[100]};color:${theme.colors.success[700]};`;
      case 'warning':
        return `background:${theme.colors.warning[100]};color:${theme.colors.warning[700]};`;
      case 'info':
        return `background:${theme.colors.primary[100]};color:${theme.colors.primary[700]};`;
      default:
        return `background:${theme.colors.backgroundSecondary};color:${theme.colors.textMuted};`;
    }
  }}
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.03em;
  ${({ $status }) => {
    switch ($status) {
      case 'refined':
        return `background:${theme.colors.success[100]};color:${theme.colors.success[700]};`;
      case 'drafted':
        return `background:${theme.colors.primary[100]};color:${theme.colors.primary[700]};`;
      case 'needs_draft':
        return `background:${theme.colors.warning[100]};color:${theme.colors.warning[700]};`;
      case 'approved':
        return `background:${theme.colors.success[100]};color:${theme.colors.success[700]};`;
      case 'user_excluded':
        return `background:${theme.colors.backgroundSecondary};color:${theme.colors.textMuted};`;
      default:
        return `background:${theme.colors.backgroundSecondary};color:${theme.colors.textMuted};`;
    }
  }}
`;

const QuestionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 480px;
  .text {
    color: ${theme.colors.textPrimary};
    line-height: ${theme.typography.lineHeight.tight};
  }
  .meta {
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const ScoreCell = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary[600]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  &:hover { text-decoration: underline; }
  &:disabled {
    color: ${theme.colors.textMuted};
    cursor: progress;
    text-decoration: none;
  }
`;

const EmptyState = styled.div`
  padding: ${theme.spacing[8]};
  text-align: center;
  color: ${theme.colors.textMuted};
`;

const STATUS_LABEL: Record<string, string> = {
  refined: 'Refined',
  drafted: 'Drafted',
  needs_draft: 'Awaiting draft',
  user_excluded: 'Excluded',
  approved: 'Approved',
};

const STATUS_TOOLTIP: Record<string, string> = {
  refined: 'AI improved an existing bidder response using knowledge-base patterns.',
  drafted: 'AI generated a first draft from scratch (no inline response in the source).',
  needs_draft:
    'Question was extracted but no inline response exists yet and the auto-draft step has not produced one. Re-run analysis or upload supporting KB content so the AI can draft a response.',
  user_excluded: 'You marked this question as not applicable to this bid.',
  approved: 'You approved the AI-produced response as final.',
};

const typeTone = (t?: string): 'success' | 'warning' | 'info' | 'neutral' => {
  if (!t) return 'neutral';
  const v = t.toLowerCase();
  if (v.includes('must')) return 'success';
  if (v.includes('should')) return 'info';
  if (v.includes('could')) return 'warning';
  return 'neutral';
};

interface Props {
  questions?: Question[];
  refinedResponses?: RefinedResponse[];
  draftedQuestions?: DraftedQuestion[];
  evaluationCriteria?: EvaluationCriteria[];
  selectionMetadata?: Record<string, QuestionSelectionMetadata>;
  complianceItems?: ComplianceItem[];
  pastTenderMatches?: Record<string, PastTenderMatch[]>;
  refinementPathUsed?: 'generic_best_practices' | 'tender_bank_patterns' | null;
  /** Non-blocking warnings emitted by HITL nodes that were skipped via
   *  configuration (default for fresh tenders). Rendered as a banner above
   *  the table so the bidder sees critical KB gaps / compliance issues /
   *  draft summary stats without the run hanging on an interrupt. */
  workflowWarnings?: WorkflowWarning[];
  /** AI model id for PPN-compliance disclosure in the drawer. */
  modelId?: string;
  /** Thread id used to scope localStorage state for the compliance checklist. */
  threadId?: string;
  /** True when the run was started in GENERATE mode — every answer was
   *  authored from scratch, none refined from inline content. Drives label
   *  swaps: 'Refined' → 'Generated', subhead removes the 'refined' framing,
   *  etc. Matches the same flag we use to hide the Initial Assessment tab. */
  isGenerateMode?: boolean;
  /** Threaded down to ResponseComparisonCard so AI Refine, Regenerate, and
   *  Accept work from the question detail drawer. */
  onHumanRefine?: (
    questionId: string,
    prompt: string,
    callbacks: {
      onChunk: (chunk: string) => void;
      onComplete: (finalResponse: string) => void;
      onError: (error: Error) => void;
    }
  ) => void;
  onAcceptRefinement?: (questionId: string, refinedText: string) => void;
}

type SortKey = 'status' | 'type' | 'criterion' | 'score' | 'id';

const QuestionMatrix: React.FC<Props> = ({
  questions = [],
  refinedResponses = [],
  draftedQuestions = [],
  evaluationCriteria,
  selectionMetadata,
  complianceItems = [],
  pastTenderMatches,
  refinementPathUsed,
  workflowWarnings = [],
  modelId,
  threadId,
  isGenerateMode = false,
  onHumanRefine,
  onAcceptRefinement,
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [criterionFilter, setCriterionFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Inline row expansion — only one question is expanded at a time. Click
  // the same row again (or its View button) to collapse. Replaces the previous
  // right-slide modal drawer; reviewing an AI draft is workspace work, not a
  // peek, so we keep the user inside the matrix.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggleExpanded = useCallback(
    (qid: string) => setExpandedId((curr) => (curr === qid ? null : qid)),
    [],
  );
  // Per-question on-demand drafting state. Keyed by question_id so multiple
  // rows can be drafted in parallel without their spinners stomping on each
  // other. The map holds the draft entry returned from the backend so we can
  // optimistically merge it into draftedQuestions on success — the row flips
  // `needs_draft → drafted` without waiting for a full state refresh.
  const [draftingIds, setDraftingIds] = useState<Set<string>>(new Set());
  const [localDrafts, setLocalDrafts] = useState<DraftedQuestion[]>([]);
  const [draftError, setDraftError] = useState<string | null>(null);

  const handleDraftNow = async (row: QuestionMatrixRow) => {
    if (!threadId) {
      setDraftError(
        'Cannot draft this question: no active analysis session. Re-open from the Tender Response page.',
      );
      return;
    }
    if (draftingIds.has(row.question_id)) return;

    setDraftError(null);
    setDraftingIds((prev) => {
      const next = new Set(prev);
      next.add(row.question_id);
      return next;
    });

    try {
      const { draft } = await aiApiService.draftSingleQuestion(threadId, row.question_id);
      setLocalDrafts((prev) => {
        const filtered = prev.filter((d) => d.question_id !== draft.question_id);
        return [...filtered, draft as DraftedQuestion];
      });
    } catch (err) {
      console.error('Single-question draft failed:', err);
      setDraftError(
        err instanceof Error
          ? err.message
          : 'Draft generation failed. Please try again or re-run the analysis.',
      );
    } finally {
      setDraftingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.question_id);
        return next;
      });
    }
  };

  // Merge local on-demand drafts on top of the backend-provided drafted
  // questions so the row flips status without waiting for a state refresh.
  const mergedDrafted = useMemo<DraftedQuestion[]>(() => {
    const out: DraftedQuestion[] = [...(draftedQuestions || [])];
    for (const ld of localDrafts) {
      const idx = out.findIndex((d) => d.question_id === ld.question_id);
      if (idx >= 0) out[idx] = ld;
      else out.push(ld);
    }
    return out;
  }, [draftedQuestions, localDrafts]);

  const rows = useMemo(
    () =>
      buildQuestionMatrix({
        questions,
        refinedQuestions: refinedResponses as any,
        draftedQuestions: mergedDrafted,
        evaluationCriteria,
        selectionMetadata,
        pastTenderMatches,
        responseMode: isGenerateMode ? 'generate' : undefined,
      }),
    [questions, refinedResponses, mergedDrafted, evaluationCriteria, selectionMetadata, pastTenderMatches, isGenerateMode]
  );

  const criterionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.criterion_label) set.add(r.criterion_label);
    }
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows;
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (typeFilter !== 'all')
      list = list.filter((r) => (r.type || '').toLowerCase() === typeFilter);
    if (criterionFilter !== 'all')
      list = list.filter((r) => r.criterion_label === criterionFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.question.toLowerCase().includes(q) ||
          r.question_id.toLowerCase().includes(q) ||
          (r.section || '').toLowerCase().includes(q) ||
          (r.criterion_label || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case 'status': {
          const av = STATUS_SORT_ORDER[a.status] ?? 99;
          const bv = STATUS_SORT_ORDER[b.status] ?? 99;
          if (av !== bv) return (av - bv) * dir;
          return a.question_id.localeCompare(b.question_id);
        }
        case 'type':
          return ((a.type || '') > (b.type || '') ? 1 : -1) * dir;
        case 'criterion':
          return ((a.criterion_label || '') > (b.criterion_label || '') ? 1 : -1) * dir;
        case 'score': {
          const as = typeof a.improvement_score === 'number' ? a.improvement_score : -1;
          const bs = typeof b.improvement_score === 'number' ? b.improvement_score : -1;
          return (as - bs) * dir;
        }
        case 'id':
        default:
          return a.question_id.localeCompare(b.question_id) * dir;
      }
    });
    return list;
  }, [rows, statusFilter, typeFilter, criterionFilter, search, sortKey, sortDir]);

  // Stats — counted from the unfiltered set so they stay informative.
  const stats = useMemo(() => {
    const total = rows.length;
    const refined = rows.filter((r) => r.status === 'refined').length;
    const drafted = rows.filter((r) => r.status === 'drafted').length;
    const needs = rows.filter((r) => r.status === 'needs_draft').length;
    const scored = rows
      .map((r) => r.improvement_score)
      .filter((s): s is number => typeof s === 'number');
    const avg = scored.length
      ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length)
      : null;
    return { total, refined, drafted, needs, avg };
  }, [rows]);

  // P5 — Total-tender pacing. Sum word counts across all responses; compare
  // against the page-limit-derived budget extracted from compliance items.
  const tenderPacing = useMemo(() => {
    const totalWords = rows.reduce(
      (acc, r) => acc + countWords(r.current_response || ''),
      0
    );
    let pageLimit: number | null = null;
    for (const c of complianceItems || []) {
      const found = extractPageLimitFromText(c.text);
      if (found) {
        pageLimit = found;
        break;
      }
    }
    const budgetWords = pageLimit ? estimateWordBudgetFromPages(pageLimit) : null;
    const pct = budgetWords ? Math.round((totalWords / budgetWords) * 100) : null;
    return { totalWords, pageLimit, budgetWords, pct };
  }, [rows, complianceItems]);

  // Knowledge-base grounding warning — fires when drafts exist but none
  // carry `sources`. The previous copy said "KB is empty", which was
  // misleading: the KB might be populated (as it usually is) but the
  // retrieval step might not have surfaced anything for this analysis
  // (e.g. wiring bug, bad embeddings, irrelevant content). The new copy
  // is honest about both possibilities and points to the Manage page so
  // the user can verify.
  const showEmptyKbWarning = useMemo(() => {
    const drafts = (draftedQuestions as any[]) || [];
    if (!drafts.length) return false;

    // Suppress the broad "KB not used" warning when there's clear evidence
    // KB retrieval did fire elsewhere in the run. Two reliable signals:
    //   1. Any drafted response carries `sources` — drafting cited the KB.
    //   2. `pastTenderMatches` has at least one match — match_past_tenders
    //      successfully retrieved from the company KB / Tender Bank store
    //      (it's the same retrieval pipeline; an empty result here means
    //      nothing relevant was found, not that retrieval was bypassed).
    //   3. Any refined response carries `kb_examples_used > 0` —
    //      refine_again injected KB chunks (when it ran).
    // Without these checks the warning lied: a run where generic_refinement
    // pre-fetched 50 KB+TB chunks for refinement still tripped the warning
    // because the 1 drafted response happened to have no `sources` field.
    const anyDraftWithSources = drafts.some(
      (d) => Array.isArray(d?.sources) && d.sources.length > 0
    );
    if (anyDraftWithSources) return false;

    const refined = (refinedResponses as any[]) || [];
    const anyRefinedUsedKb = refined.some(
      (r) =>
        (typeof r?.kb_examples_used === 'number' && r.kb_examples_used > 0) ||
        (typeof r?.past_tender_chunks_used === 'number' && r.past_tender_chunks_used > 0)
    );
    if (anyRefinedUsedKb) return false;

    const matches = pastTenderMatches || {};
    const anyPastMatch = Object.values(matches).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
    if (anyPastMatch) return false;

    return true;
  }, [draftedQuestions, refinedResponses, pastTenderMatches]);

  const handleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir('asc');
    }
  };

  const handleExport = () => {
    const csv = questionMatrixToCsv(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tender_question_matrix_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportWord = async () => {
    if (!threadId) {
      alert(
        "We can't export this analysis to Word until it's saved to a thread. " +
        'Run a fresh analysis first.'
      );
      return;
    }
    const apiBaseUrl =
      process.env.REACT_APP_API_BASE_URL ||
      (process.env.NODE_ENV === 'production'
        ? 'https://api-beta-open-tender.nat-quest.com'
        : 'http://localhost:2024');
    const tokenRaw = localStorage.getItem('opentender_tokens');
    const headers: Record<string, string> = {};
    if (tokenRaw) {
      try {
        const tok = JSON.parse(tokenRaw);
        if (tok?.accessToken) headers['Authorization'] = `Bearer ${tok.accessToken}`;
      } catch {}
    }
    try {
      const resp = await fetch(`${apiBaseUrl}/api/export/sessions/${threadId}/word`, {
        method: 'GET',
        headers,
      });
      if (!resp.ok) {
        const text = await resp.text();
        alert(`Word export failed: ${resp.status} ${text.slice(0, 200)}`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tender_response_${threadId.slice(0, 8)}_${new Date()
        .toISOString()
        .slice(0, 10)}.docx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Word export error: ${e?.message || e}`);
    }
  };

  // GENERATE-mode subhead drops the "refined" framing entirely — every answer
  // was authored from scratch from the company knowledge base, so the
  // before/after refinement language doesn't apply. Falls through to the
  // normal `refinementPathUsed` switch when not GENERATE mode.
  const subhead = isGenerateMode
    ? 'AI drafted every response from your knowledge base — there was no inline content to refine.'
    : refinementPathUsed === 'tender_bank_patterns'
    ? 'Refined using successful tender patterns from your tender bank; drafted from your knowledge base where there was no inline answer.'
    : refinementPathUsed === 'generic_best_practices'
    ? 'Refined using general best-practice rules; drafted from your knowledge base where there was no inline answer.'
    : 'AI co-author for every extracted question — refined where the bidder had content, drafted where they didn\'t.';

  return (
    <Container>
      <OverviewSection>
        <OverviewHeader>
          <OverviewIcon>
            <Sparkles size={24} />
          </OverviewIcon>
          <OverviewContent>
            <h2>Question Matrix</h2>
            <p>{subhead}</p>
          </OverviewContent>
        </OverviewHeader>

        <StatsGrid>
          <StatCard>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total questions</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.refined}</StatValue>
            {/* In GENERATE mode the items in this slot were drafted-then-polished,
                not refined from inline bidder content — so the 'Refined' label
                is misleading. Show 'Generated' instead; the underlying count
                stays the same (state.refined_questions). */}
            <StatLabel>{isGenerateMode ? 'Generated' : 'Refined'}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.drafted}</StatValue>
            <StatLabel>Drafted</StatLabel>
          </StatCard>
          <StatCard
            title="Questions extracted from the tender that have no inline bidder response and have not yet been auto-drafted."
          >
            <StatValue>{stats.needs}</StatValue>
            <StatLabel>Awaiting draft</StatLabel>
          </StatCard>
          <StatCard
            title={
              'Average per-question improvement signal — how much extra specificity ' +
              '(named projects, numbers, certifications, dates) the AI added on top of the ' +
              'original wording. This is a heuristic delta in [0, 100]; values typically ' +
              'land in single digits because most refinements are wording polish, not ' +
              'wholesale evidence injection. For the overall before/after quality score ' +
              '(0–100 anchored rubric), see the Performance Analysis tab.'
            }
          >
            <StatValue>{stats.avg === null ? '—' : `${stats.avg}%`}</StatValue>
            <StatLabel>Avg improvement</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Total-tender pacing — sums word counts across every response and
            compares against the page-limit budget extracted from the tender's
            compliance items. Surfaces only when both numbers are available. */}
        {tenderPacing.budgetWords && (
          <div
            style={{
              marginTop: theme.spacing[4],
              padding: theme.spacing[3],
              background: 'white',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.md,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing[2],
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: theme.typography.fontWeight.semibold,
              }}
            >
              <span>Total response length</span>
              <span>
                {tenderPacing.totalWords.toLocaleString()} of ~{tenderPacing.budgetWords.toLocaleString()} words
                {tenderPacing.pageLimit ? ` (${tenderPacing.pageLimit}-page limit)` : ''}
              </span>
            </div>
            <div
              style={{
                height: 8,
                background: theme.colors.backgroundSecondary,
                borderRadius: theme.borderRadius.full,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, tenderPacing.pct ?? 0)}%`,
                  background:
                    (tenderPacing.pct ?? 0) > 100
                      ? theme.colors.error[500]
                      : (tenderPacing.pct ?? 0) > 85
                      ? theme.colors.warning[500]
                      : theme.colors.success[500],
                  transition: `width ${theme.transitions.base}`,
                }}
              />
            </div>
            {(tenderPacing.pct ?? 0) > 100 && (
              <div
                style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.colors.error[600],
                  fontWeight: theme.typography.fontWeight.medium,
                }}
              >
                ⚠ Total length exceeds the tender's {tenderPacing.pageLimit}-page limit. Tighten responses
                before submission.
              </div>
            )}
          </div>
        )}

        {/* Empty-KB warning — drafts produced without any KB sources are
            generic by definition. Nudge the bidder to populate the KB. */}
        {showEmptyKbWarning && (
          <div
            style={{
              marginTop: theme.spacing[4],
              padding: theme.spacing[3],
              background: `${theme.colors.warning[100]}80`,
              border: `1px solid ${theme.colors.warning[500]}40`,
              borderRadius: theme.borderRadius.md,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.warning[700],
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing[1],
            }}
          >
            <strong>The auto-drafted responses weren't grounded in your knowledge base.</strong>
            <span>
              None of the drafts above carry KB citations. This can happen for two reasons:
              {' '}your knowledge base hasn't been populated yet, OR the analysis ran without
              activating KB retrieval (a known wiring bug fixed in recent versions — re-run
              the analysis to confirm). Verify your KB and re-run for drafts that cite your
              actual case studies, certifications, and team bios.{' '}
              <a
                href="/knowledge-base"
                style={{ color: theme.colors.primary[700], textDecoration: 'underline' }}
              >
                Manage Knowledge Base →
              </a>
            </span>
          </div>
        )}
      </OverviewSection>

      {workflowWarnings && workflowWarnings.length > 0 && (
        <div
          role="region"
          aria-label="Workflow warnings"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing[2],
            marginTop: theme.spacing[3],
          }}
        >
          {workflowWarnings.map((w, i) => {
            const tone =
              w.severity === 'high' || w.severity === 'critical'
                ? {
                    bg: `${theme.colors.error[100]}80`,
                    border: `${theme.colors.error[500]}40`,
                    fg: theme.colors.error[700],
                  }
                : w.severity === 'medium' || w.severity === 'warning'
                ? {
                    bg: `${theme.colors.warning[100]}80`,
                    border: `${theme.colors.warning[500]}40`,
                    fg: theme.colors.warning[700],
                  }
                : {
                    bg: `${theme.colors.primary[100]}40`,
                    border: `${theme.colors.primary[500]}40`,
                    fg: theme.colors.primary[700],
                  };
            const titleByType: Record<string, string> = {
              knowledge_gaps: 'Knowledge base gaps detected',
              compliance_issues: 'Compliance issues flagged',
              draft_summary: 'AI drafts ready for review',
              low_content_refine: 'Low content for refinement',
              kb_empty_ungrounded: 'Knowledge base is empty — drafts are ungrounded scaffolds',
            };
            return (
              <div
                key={`${w.type}-${i}`}
                style={{
                  padding: theme.spacing[3],
                  background: tone.bg,
                  border: `1px solid ${tone.border}`,
                  borderRadius: theme.borderRadius.md,
                  color: tone.fg,
                  fontSize: theme.typography.fontSize.sm,
                  lineHeight: theme.typography.lineHeight.relaxed,
                }}
              >
                <div style={{ fontWeight: theme.typography.fontWeight.semibold, marginBottom: 2 }}>
                  {titleByType[w.type] || 'Workflow notice'}
                </div>
                <div>{w.message}</div>
              </div>
            );
          })}
        </div>
      )}

      <ControlsRow>
        <SearchInput>
          <Search size={16} />
          <input
            type="text"
            placeholder="Search questions, sections, criteria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </SearchInput>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="refined">Refined</option>
          <option value="drafted">Drafted</option>
          <option value="needs_draft">Awaiting draft</option>
          <option value="user_excluded">Excluded</option>
        </Select>
        <Select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filter by question type"
        >
          <option value="all">All types</option>
          <option value="must">Must</option>
          <option value="should">Should</option>
          <option value="could">Could</option>
          <option value="pass-fail">Pass-Fail</option>
        </Select>
        {criterionOptions.length > 0 && (
          <Select
            value={criterionFilter}
            onChange={(e) => setCriterionFilter(e.target.value)}
            aria-label="Filter by criterion"
          >
            <option value="all">All criteria</option>
            {criterionOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        )}
        <ExportButton onClick={handleExport} title="Export the visible rows as CSV">
          <Download size={16} />
          Export CSV
        </ExportButton>
        <ExportButton
          onClick={handleExportWord}
          title="Export the full proposal as a Word document, grouped by tender section"
          style={{
            background: theme.colors.success[600],
          }}
        >
          <FileText size={16} />
          Export Word
        </ExportButton>
      </ControlsRow>

      {draftError && (
        <div
          role="alert"
          style={{
            marginTop: theme.spacing[3],
            padding: theme.spacing[3],
            background: `${theme.colors.error[100]}80`,
            border: `1px solid ${theme.colors.error[500]}40`,
            borderRadius: theme.borderRadius.md,
            color: theme.colors.error[700],
            fontSize: theme.typography.fontSize.sm,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: theme.spacing[3],
          }}
        >
          <span>{draftError}</span>
          <button
            onClick={() => setDraftError(null)}
            style={{
              background: 'none',
              border: `1px solid ${theme.colors.error[500]}40`,
              color: theme.colors.error[700],
              borderRadius: theme.borderRadius.sm,
              padding: '2px 8px',
              cursor: 'pointer',
              fontSize: theme.typography.fontSize.xs,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      <TableWrapper>
        {filtered.length === 0 ? (
          <EmptyState>
            <Filter size={28} style={{ marginBottom: 12, opacity: 0.5 }} />
            <p>No questions match your filters.</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th $sortable onClick={() => handleSort('id')}>
                  ID <ArrowUpDown size={12} style={{ verticalAlign: 'middle' }} />
                </Th>
                <Th>Question</Th>
                <Th $sortable onClick={() => handleSort('type')}>
                  Type
                </Th>
                <Th $sortable onClick={() => handleSort('criterion')}>
                  Criterion
                </Th>
                <Th $sortable onClick={() => handleSort('status')}>
                  Status
                </Th>
                <Th $sortable onClick={() => handleSort('score')}>
                  Score
                </Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const isExpanded = expandedId === row.question_id;
                const canExpand = row.status !== 'needs_draft';
                return (
                <React.Fragment key={row.question_id}>
                <Tr
                  $expanded={isExpanded}
                  onClick={() => canExpand && toggleExpanded(row.question_id)}
                >
                  <Td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {canExpand && (
                        <ChevronIcon $expanded={isExpanded} aria-hidden>
                          <ChevronRight size={14} color={theme.colors.textMuted} />
                        </ChevronIcon>
                      )}
                      <QId>{row.question_id}</QId>
                    </span>
                  </Td>
                  <Td>
                    <QuestionText>
                      <span className="text">
                        {row.question.length > 140
                          ? `${row.question.slice(0, 140)}…`
                          : row.question}
                      </span>
                      <span className="meta">
                        {row.section ? `${row.section}` : ''}
                        {row.suggested_words ? ` · ~${row.suggested_words} words` : ''}
                      </span>
                    </QuestionText>
                  </Td>
                  <Td>
                    {row.type ? (
                      <TypeBadge $tone={typeTone(row.type)}>{row.type}</TypeBadge>
                    ) : (
                      <span style={{ color: theme.colors.textMuted }}>—</span>
                    )}
                  </Td>
                  <Td>
                    {row.criterion_label ? (
                      <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.textPrimary }}>
                        {row.criterion_label}
                      </span>
                    ) : (
                      <span style={{ color: theme.colors.textMuted }}>—</span>
                    )}
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                      <StatusBadge
                        $status={row.status}
                        title={STATUS_TOOLTIP[row.status] || ''}
                      >
                        {row.status === 'refined' && <FileText size={12} />}
                        {row.status === 'drafted' && <Sparkles size={12} />}
                        {row.status === 'needs_draft' && <AlertCircle size={12} />}
                        {STATUS_LABEL[row.status] || row.status}
                      </StatusBadge>
                      {/* Ungrounded badge — drafts authored from scratch when
                          the company KB was empty. The bidder MUST replace
                          bracketed placeholders before submission. */}
                      {row.kb_retrieval?.coverage?.assessment === 'ungrounded' && (
                        <span
                          title="No company-specific evidence cited — KB was empty at draft time. Replace [BRACKETED PLACEHOLDERS] with your real specifics before submission."
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '2px 6px',
                            background: `${theme.colors.error[100]}80`,
                            border: `1px solid ${theme.colors.error[500]}66`,
                            borderRadius: theme.borderRadius.sm,
                            color: theme.colors.error[700],
                            fontSize: 10,
                            fontWeight: theme.typography.fontWeight.semibold,
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                          }}
                        >
                          <AlertCircle size={10} /> Ungrounded
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <ScoreCell
                      style={{
                        color:
                          typeof row.improvement_score === 'number'
                            ? row.improvement_score >= 80
                              ? theme.colors.success[700]
                              : row.improvement_score >= 60
                              ? theme.colors.primary[700]
                              : theme.colors.warning[700]
                            : theme.colors.textMuted,
                      }}
                    >
                      {typeof row.improvement_score === 'number'
                        ? `${row.improvement_score}%`
                        : '—'}
                    </ScoreCell>
                  </Td>
                  <Td>
                    {(() => {
                      const isDrafting = draftingIds.has(row.question_id);
                      if (row.status === 'needs_draft') {
                        return (
                          <ActionLink
                            disabled={isDrafting}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDraftNow(row);
                            }}
                            title={
                              isDrafting
                                ? 'Generating a draft using your knowledge base — this typically takes 10–20s.'
                                : 'Generate a draft now using the same AI pipeline that produced the bulk drafts.'
                            }
                          >
                            <Sparkles size={14} />
                            {isDrafting ? 'Drafting…' : 'Draft now'}
                          </ActionLink>
                        );
                      }
                      return (
                        <ActionLink
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(row.question_id);
                          }}
                        >
                          {isExpanded ? (
                            <>
                              <ChevronDown size={14} /> Hide
                            </>
                          ) : row.status === 'refined' ? (
                            <>
                              <Eye size={14} /> View
                            </>
                          ) : row.status === 'drafted' ? (
                            <>
                              <Edit3 size={14} /> Review draft
                            </>
                          ) : (
                            <>
                              <Eye size={14} /> View
                            </>
                          )}
                        </ActionLink>
                      );
                    })()}
                  </Td>
                </Tr>
                {isExpanded && (
                  <ExpandedRow>
                    <td colSpan={7}>
                      <ExpandedCell>
                        <QuestionDetailDrawer
                          row={row}
                          onClose={() => setExpandedId(null)}
                          modelId={modelId}
                          onHumanRefine={onHumanRefine}
                          onAcceptRefinement={onAcceptRefinement}
                          mode="inline"
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: theme.spacing[3] }}>
                          <ActionLink
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(null);
                            }}
                          >
                            <X size={14} /> Collapse
                          </ActionLink>
                        </div>
                      </ExpandedCell>
                    </td>
                  </ExpandedRow>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        )}
      </TableWrapper>

      {complianceItems.length > 0 && (
        <ComplianceChecklist
          items={complianceItems}
          storageKey={threadId ? `opentender_compliance_${threadId}` : undefined}
        />
      )}

      {/* Drawer mount removed — question details now render inline as an
          expanded row directly under the clicked question (see ExpandedRow
          above). The QuestionDetailDrawer component is still imported and
          rendered with mode="inline" inside ExpandedCell. */}
    </Container>
  );
};

export default QuestionMatrix;
